import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

export default function Hero() {
  const { t } = useTranslation();
  const [cardsVisible, setCardsVisible] = useState<boolean[]>([false, false, false, false]);
  const [heroLoaded, setHeroLoaded] = useState(false);
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Staggered animation for hero content and cards
  useEffect(() => {
    // Hero content fade in
    const heroTimer = setTimeout(() => {
      setHeroLoaded(true);
    }, 100);

    // Cards staggered animation (1 second each)
    const cardTimers = cardsVisible.map((_, index) => {
      return setTimeout(() => {
        setCardsVisible(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, 800 + (index * 1000)); // Start after 800ms, then 1s apart
    });

    return () => {
      clearTimeout(heroTimer);
      cardTimers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const serviceCards = [
    {
      icon: 'ri-tools-line',
      title: t('hero.card1Title'),
      desc: t('hero.card1Desc')
    },
    {
      icon: 'ri-store-2-line',
      title: t('hero.card2Title'),
      desc: t('hero.card2Desc')
    },
    {
      icon: 'ri-building-line',
      title: t('hero.card3Title'),
      desc: t('hero.card3Desc')
    },
    {
      icon: 'ri-sparkling-line',
      title: t('hero.card4Title'),
      desc: t('hero.card4Desc')
    }
  ];

  return (
    <section id="hero" className="relative min-h-screen md:min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/e0b82875b13fae9b210ebd1e2023b3e5.jpg" 
          alt="Professional office cleaning service" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/85 to-[#0F172A]/70"></div>
      </div>

      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl hidden md:block"></div>
      <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-[#60A5FA]/10 rounded-full blur-3xl hidden md:block"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className={`transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-sm rounded-full mb-4 md:mb-6">
              <span className="text-white/90 text-xs md:text-sm font-medium">{t('hero.badge')}</span>
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
              {t('hero.title1')}
              <span className="block bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] bg-clip-text text-transparent">{t('hero.title2')}</span>
            </h1>

            <p className="text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-xl leading-relaxed">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-12">
              <button onClick={() => scrollToSection('contact')} className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-md bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] text-white shadow group">
                {t('hero.cta1')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </button>
              <button onClick={() => scrollToSection('services')} className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-md border border-white/30 text-white hover:bg-white/10">
                {t('hero.cta2')}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 md:gap-8">
              <div className="text-center">
                <div className="w-12 h-12 md:w-20 md:h-20 mx-auto rounded-xl md:rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center mb-2 md:mb-3">
                  <i className="ri-award-line text-xl md:text-4xl text-white"></i>
                </div>
                <div className="text-xs md:text-base text-white/70 font-medium">{t('hero.stat1Label')}</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 md:w-20 md:h-20 mx-auto rounded-xl md:rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center mb-2 md:mb-3">
                  <i className="ri-team-line text-xl md:text-4xl text-white"></i>
                </div>
                <div className="text-xs md:text-base text-white/70 font-medium">{t('hero.stat2Label')}</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 md:w-20 md:h-20 mx-auto rounded-xl md:rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center mb-2 md:mb-3">
                  <i className="ri-heart-line text-xl md:text-4xl text-white"></i>
                </div>
                <div className="text-xs md:text-base text-white/70 font-medium">{t('hero.stat3Label')}</div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="flex flex-col gap-4">
              {serviceCards.map((card, index) => (
                <div 
                  key={index}
                  className={`bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-700 cursor-pointer group ${
                    cardsVisible[index] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <i className={`${card.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">{card.title}</h3>
                      <p className="text-white/70 text-xs leading-relaxed">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2 animate-bounce">
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
